import { Button } from "@/ui/atoms/Button";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/utils/utils";

export type TPaginationProps = {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    className?: string;
    /** `total` is a lower bound: more rows exist than were counted. Shown as "N+",
        and Next stays enabled past the last counted page. */
    totalIsLowerBound?: boolean;
};

export function Pagination({
    page,
    pageSize,
    total,
    onPageChange,
    className,
    totalIsLowerBound = false,
}: TPaginationProps) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    const hasNext = totalIsLowerBound || page < totalPages;
    const plus = totalIsLowerBound ? "+" : "";

    if (total <= pageSize && !totalIsLowerBound) return null;

    return (
        <div
            className={cn(
                "flex items-center justify-between text-sm text-muted-foreground",
                className,
            )}
        >
            <span>
                Showing {start}&ndash;{end} of {total}
                {plus}
            </span>
            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => onPageChange(1)}
                    aria-label="First page"
                >
                    <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                    aria-label="Previous page"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 tabular-nums">
                    Page {page} of {totalPages}
                    {plus}
                </span>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasNext}
                    onClick={() => onPageChange(page + 1)}
                    aria-label="Next page"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => onPageChange(totalPages)}
                    aria-label="Last page"
                >
                    <ChevronsRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}
